import React from 'react';
import format from 'date-fns/format';
import { DocumentAccessLevel } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNACard, DNAText, DNAButton, useTheme, Iffy } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import {
  DNACustomFieldChipList,
  DNADocumentThumbnail,
  DNADocumentContextMenu,
  useDNADocumentActions,
} from 'src/components/DNA';
import { DocumentORM } from 'src/types/types';
import { DNARowsComponent } from 'src/components/DNA/FlatList/Rows/types';
import { CustomLabelDisplay } from './CustomLabelDisplay';

export type DNADocumentDefaultRowProps = {
  onPin?: () => void,
  isDesktop?: boolean;
}

const DNADocumentDefaultRow: DNARowsComponent<
  DocumentORM,
  DNADocumentDefaultRowProps
> = (props) => {
  const {
    ORM: documentORM,
    onPin,
    onPress,
    style,
    isDesktop,
    variant,
  } = props
  const hideControls = variant === 'dashboardFavorites'

  const theme = useTheme()

  const isBookmarked = documentORM.meta.bookmark.isBookmarked
  const documentActions = useDNADocumentActions()
  const isPersonalDocument = documentORM.model.accessLevel === DocumentAccessLevel.USER;
  const documentVersionORM =
    documentORM.relations.version.latestPublishedDocumentVersionORM ||
    documentORM.relations.version.latestDocumentVersionORM;

  return (
    <DNACard
      appearance="flat"
      interactive="pressable"
      onPress={() => { onPress?.(documentORM) }}
      style={[
        {
          paddingHorizontal: theme['row-padding-horizontal'],
          paddingVertical: theme['row-padding-vertical'],
        },
        style,
      ]}
    >
      <DNABox testID="folder-item-row" alignY="center" spacing="between" childFill={0}>
        <DNABox alignY="center" spacing="lg" childFill={1}>
          <DNABox spacing="lg">
            <Iffy is={!isDesktop && !hideControls}>
              <DNADocumentContextMenu documentORM={documentORM}>
                <DNAButton
                  appearance="ghost"
                  status="subtle"
                  size="xl"
                  iconLeft="dots-three-vertical-bold"
                />
              </DNADocumentContextMenu>
            </Iffy>
            <DNADocumentThumbnail.Hover
              documentVersionORM={documentVersionORM}
              width={120}
              height={68}
              showProcessing
              // Assessing implementation in #BEAC-2155
              // onPress={() => { console.log('pressed default row') }}
            />
          </DNABox>
          {/* Title */}
          <DNABox
            spacing="sm"
            appearance="col"
            childFill
            style={{ paddingRight: 16 }}
          >
            <DNAText testID="folder-item-row-name" b1 numberOfLines={1}>
              {documentORM.relations.version.latestPublishedDocumentVersionORM?.model.title}
            </DNAText>
            {!isPersonalDocument &&
              <DNABox alignY="center" spacing="sm" fill childFill={1}>
                <DNACustomFieldChipList
                  documentVersionORM={documentORM.relations.version.latestUsableDocumentVersionORM}
                />
                <CustomLabelDisplay documentORM={documentORM} />
              </DNABox>}
          </DNABox>
        </DNABox>
        {/* Actions */}
        <DNABox spacing="sm" alignY="center">
          <Iffy is={isPersonalDocument}>
            <DNAText testID="folder-item-row-date" style={{ color: colors['color-text-secondary'], marginRight: 57 }}>
              {format(new Date(documentORM.model.createdAt), 'MM/dd/yyyy')}
            </DNAText>
          </Iffy>
          <Iffy is={!hideControls}>
            <Iffy is={!isPersonalDocument}>
              <DNAButton
                appearance="ghost"
                status={isBookmarked ? 'primary' : 'subtle'}
                onPress={() => {
                  documentActions.bookmark(documentORM)()
                  onPin?.()
                }}
                iconLeft={isBookmarked ? 'star-fill' : 'star'}
              />
            </Iffy>
          </Iffy>
          <Iffy is={isDesktop && !hideControls}>
            <DNADocumentContextMenu documentORM={documentORM}>
              <DNAButton
                appearance="ghost"
                status="subtle"
                iconLeft="dots-three-vertical-bold"
              />
            </DNADocumentContextMenu>
          </Iffy>
        </DNABox>
      </DNABox>
    </DNACard>
  )
}

export default DNADocumentDefaultRow
